// src/pages/TestPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import {
  ArrowLeft,
  BookOpen,
  Home,
  X,
  CheckCircle2,
  XCircle,
  PartyPopper,
} from "lucide-react";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

import TestCard from "./TestCard";
import {
  submitTestAttempt,
  markProgressVisit,
  markProgressComplete,
  API_BASE,
} from "../services/api";

// Hidden system defaults (kept for any internal logic you may add later)
const MAX_ATTEMPTS = 4;
const PASS_MARK = 50;

const COURSE_TOTALS = {
  discover: 26,
  ugunduzi: 26,
  "daniel-revelation": 21,
  health: 20,
  family: 16,
  "focus-family": 16, // normalized to 'family' for grading/cert
};

const attemptsKey = (courseId, lessonId) => `vop_test_attempts_${courseId}_${lessonId}`;
const passedKey = (courseId, lessonId) => `vop_test_passed_${courseId}_${lessonId}`;
const latestScoreKey = (courseId, lessonId) => `vop_test_latest_score_${courseId}_${lessonId}`;

// Normalize course id for server + cert issuance
function normalizeCourseId(id) {
  if (id === "focus-family") return "family";
  return id;
}

function clamp(n, min, max) {
  const x = Number(n);
  if (Number.isNaN(x)) return min;
  return Math.min(max, Math.max(min, x));
}

export default function TestPage() {
  const { courseId = "discover", lessonId = "1" } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const normCourse = normalizeCourseId(courseId);

  // 🔹 Read initial "passed" state from localStorage so we can gate the exam immediately
  const initialPassed =
    typeof window !== "undefined" &&
    localStorage.getItem(passedKey(courseId, lessonId)) === "true";

  const [userAnswers, setUserAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(null);

  const [attempts, setAttempts] = useState(() => {
    const val = parseInt(
      (typeof window !== "undefined" && localStorage.getItem(attemptsKey(courseId, lessonId))) || "0",
      10
    );
    return Number.isNaN(val) ? 0 : val;
  });

  const [passed, setPassed] = useState(initialPassed);

  // 🔹 If the learner has already passed, we start on the "result" view instead of re-offering the exam
  const [view, setView] = useState(initialPassed ? "result" : "test"); // "test" | "result"
  const [serverMessage, setServerMessage] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  // ✅ Congratulations message panel
  const [showCongrats, setShowCongrats] = useState(false);

  // 🔸 Reflection-only mode for Daniel & Revelation
  const isReflectionOnly = useMemo(() => normCourse === "daniel-revelation", [normCourse]);

  const totalLessons = useMemo(() => {
    return COURSE_TOTALS[normCourse] || COURSE_TOTALS[courseId] || 26;
  }, [normCourse, courseId]);

  const isLastLesson = useMemo(() => {
    return Number(lessonId) >= (totalLessons || 26);
  }, [lessonId, totalLessons]);

  // Safely compute the next lesson (never beyond the last)
  const nextLessonNumber = useMemo(() => {
    const total = totalLessons || 26;
    const current = Number(lessonId) || 1;
    const next = current + 1;
    return next <= total ? next : null;
  }, [lessonId, totalLessons]);

  const hasNextLesson = !!nextLessonNumber;

  const safeScore = useMemo(() => {
    if (score == null) return null;
    return clamp(score, 0, 100);
  }, [score]);

  // ✅ Show congrats ONLY when last lesson is passed/completed (or reflection course ends)
  useEffect(() => {
    // If they're on last lesson and they passed (or reflection-only), show the congrats panel.
    if (view === "result" && isLastLesson && (passed || isReflectionOnly)) {
      setShowCongrats(true);
    } else {
      setShowCongrats(false);
    }
  }, [view, isLastLesson, passed, isReflectionOnly]);

  // Load latest score from localStorage
  useEffect(() => {
    const sc =
      typeof window !== "undefined" && localStorage.getItem(latestScoreKey(courseId, lessonId));
    if (sc != null && sc !== "") setScore(Number(sc));
  }, [courseId, lessonId]);

  // Sync changes from other tabs (attempts/passed)
  useEffect(() => {
    function onStorage(e) {
      if (e.key === attemptsKey(courseId, lessonId)) {
        setAttempts(parseInt(e.newValue || "0", 10) || 0);
      }
      if (e.key === passedKey(courseId, lessonId)) {
        const nowPassed = e.newValue === "true";
        setPassed(nowPassed);
        if (nowPassed) setView("result");
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [courseId, lessonId]);

  // 🔹 Load server-side test status so gating works across devices, not just this browser
  useEffect(() => {
    let isMounted = true;

    async function loadStatus() {
      try {
        setStatusLoading(true);
        const token = await getToken().catch(() => null);
        if (!token) return;

        const url = `${API_BASE}/api/tests/status?course_slug=${encodeURIComponent(
          normCourse
        )}&lesson_number=${encodeURIComponent(lessonId)}`;

        const res = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
        });

        if (!isMounted) return;

        if (!res.ok) {
          console.warn("[TestPage] status fetch failed", res.status);
          return;
        }

        const data = await res.json().catch(() => null);
        if (!data || data.ok === false) {
          console.warn("[TestPage] status payload not ok", data);
          return;
        }

        const serverPassed = !!data.passed;
        const serverAttempts = typeof data.attempts === "number" ? data.attempts : null;
        const serverBestScore = typeof data.bestScore === "number" ? data.bestScore : null;

        if (serverAttempts != null) {
          setAttempts(serverAttempts);
          if (typeof window !== "undefined") {
            localStorage.setItem(attemptsKey(courseId, lessonId), String(serverAttempts));
          }
        }

        if (!isReflectionOnly && serverBestScore != null) {
          setScore(serverBestScore);
          if (typeof window !== "undefined") {
            localStorage.setItem(latestScoreKey(courseId, lessonId), String(serverBestScore));
          }
        }

        if (serverPassed) {
          setPassed(true);
          setView("result");
          if (typeof window !== "undefined") {
            localStorage.setItem(passedKey(courseId, lessonId), "true");
          }
        }
      } catch (err) {
        console.warn("[TestPage] error loading test status", err);
      } finally {
        if (isMounted) setStatusLoading(false);
      }
    }

    loadStatus();
    return () => {
      isMounted = false;
    };
  }, [courseId, lessonId, normCourse, getToken, isReflectionOnly]);

  // Local counters (hidden to learner)
  function bumpLocalProgress({ percent, didPass }) {
    const prevAttempts = parseInt(
      (typeof window !== "undefined" && localStorage.getItem(attemptsKey(courseId, lessonId))) ||
        "0",
      10
    );
    const newAttempts = (Number.isNaN(prevAttempts) ? 0 : prevAttempts) + 1;

    if (typeof window !== "undefined") {
      localStorage.setItem(attemptsKey(courseId, lessonId), String(newAttempts));
      localStorage.setItem(latestScoreKey(courseId, lessonId), String(percent ?? 0));
      localStorage.setItem(passedKey(courseId, lessonId), didPass ? "true" : "false");
    }

    setAttempts(newAttempts);
  }

  /**
   * Handle submission coming from the iframe:
   * We TRUST resultFromIframe — grading is done in the HTML.
   */
  async function handleIframeSubmit(answersFromIframe, resultFromIframe) {
    console.log("[TestPage] handleIframeSubmit called with:", {
      answersFromIframe,
      resultFromIframe,
      courseId,
      lessonId,
    });

    try {
      const answers = answersFromIframe || {};

      // --- 1) Use iframe result as source of truth (with reflection override) ---
      let finalScore = 0;
      let finalPassed = false;
      let totalQuestions = 0;
      let correctCount = 0;
      let passMark = PASS_MARK;

      const gradedFlag =
        typeof resultFromIframe?.graded === "boolean" ? resultFromIframe.graded : !isReflectionOnly;

      if (!gradedFlag || isReflectionOnly) {
        // 🔸 Reflection-only: treat as completed, no numeric score
        totalQuestions = Object.keys(answers).length;
        finalScore = null;
        finalPassed = true;
        correctCount = 0;
        passMark = null;
      } else if (resultFromIframe && typeof resultFromIframe === "object") {
        const {
          percent = 0,
          passed: didPass = false,
          total = 0,
          correct = 0,
          passMark: pm = PASS_MARK,
        } = resultFromIframe;

        finalScore = Number(percent) || 0;
        finalPassed = !!didPass;
        totalQuestions = Number(total) || 0;
        correctCount = Number(correct) || 0;
        passMark = Number(pm) || PASS_MARK;
      } else {
        // Fallback if a test HTML forgot to send result
        const keys = Object.keys(answers);
        totalQuestions = keys.length;
        correctCount = 0;
        finalScore = 0;
        finalPassed = false;
      }

      // --- 2) Update local progress ---
      const percentForStorage = isReflectionOnly || !gradedFlag ? 100 : finalScore || 0;
      bumpLocalProgress({
        percent: percentForStorage,
        didPass: finalPassed,
      });

      // --- 3) Update UI state ---
      setScore(isReflectionOnly ? null : finalScore);
      setPassed(finalPassed);
      setSubmitted(true);
      setUserAnswers(answers);
      setView("result");
      setServerMessage(null);

      // --- 4) Best-effort: log attempt to backend (NO grading there) ---
      try {
        const token = await getToken().catch(() => null);

        const attemptRes = await submitTestAttempt(
          {
            courseSlug: normCourse,
            lessonNumber: Number(lessonId),
            score: isReflectionOnly ? null : finalScore,
            totalQuestions,
            correctCount,
            passed: finalPassed,
            answers,
            passMark,
            graded: gradedFlag,
          },
          token
        );

        console.log("[TestPage] submitTestAttempt returned:", attemptRes);
      } catch (logErr) {
        console.warn("[tests] submitTestAttempt failed:", logErr);
        setServerMessage(
          "Your result is saved locally. Sync to server failed, please check network later."
        );
      }

      // --- 5) Best-effort: progress tracking ---
      try {
        const token = await getToken().catch(() => null);

        await markProgressVisit(
          { courseSlug: normCourse, lessonNumber: Number(lessonId) },
          token
        ).catch(() => {});

        if (finalPassed) {
          await markProgressComplete(
            { courseSlug: normCourse, lessonNumber: Number(lessonId) },
            token
          ).catch(() => {});
        }
      } catch (err) {
        console.warn("[tests] progress tracking failed:", err);
      }
    } catch (err) {
      console.error("[tests] submit/progress error:", err);
      setServerMessage("Saved locally. Sync to server failed, please check your connection.");
      setSubmitted(true);
      setView("result");
    }
  }

  function handleRetry() {
    setUserAnswers({});
    setSubmitted(false);
    setServerMessage(null);
    setView("test");
  }

  const statusPill = useMemo(() => {
    if (statusLoading) {
      return (
        <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 text-slate-700 px-3 py-1.5 text-xs font-semibold border border-slate-200">
          <span className="h-3 w-3 rounded-full border-2 border-slate-300 border-t-slate-600 animate-spin" />
          Syncing status…
        </span>
      );
    }
    if (isReflectionOnly) {
      return (
        <span className="inline-flex items-center gap-2 rounded-full bg-orange-50 text-orange-700 px-3 py-1.5 text-xs font-semibold border border-orange-200">
          Reflection Mode
        </span>
      );
    }
    if (passed) {
      return (
        <span className="inline-flex items-center gap-2 rounded-full bg-green-50 text-green-700 px-3 py-1.5 text-xs font-semibold border border-green-200">
          <CheckCircle2 size={14} />
          Passed
        </span>
      );
    }
    if (submitted) {
      return (
        <span className="inline-flex items-center gap-2 rounded-full bg-red-50 text-red-700 px-3 py-1.5 text-xs font-semibold border border-red-200">
          <XCircle size={14} />
          Not Passed
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 text-slate-700 px-3 py-1.5 text-xs font-semibold border border-slate-200">
        Assessment
      </span>
    );
  }, [statusLoading, isReflectionOnly, passed, submitted]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-white flex flex-col">
      <Navbar />

      <main className="pt-24 pb-10 px-4 sm:px-6 lg:px-8 flex-1">
        <div className="max-w-5xl mx-auto">
          {/* Top row: back + pills */}
          <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-slate-700 hover:text-orange-700 font-semibold"
            >
              <ArrowLeft size={18} />
              Back
            </button>

            <div className="flex items-center gap-2 flex-wrap">
              {statusPill}
              <span className="inline-flex items-center gap-2 rounded-full bg-white/80 text-slate-700 px-3 py-1.5 text-xs font-semibold border border-slate-200">
                Course: <span className="font-extrabold">{normCourse}</span>
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/80 text-slate-700 px-3 py-1.5 text-xs font-semibold border border-slate-200">
                Lesson: <span className="font-extrabold">{lessonId}</span> / {totalLessons}
              </span>
            </div>
          </div>

          {/* Header card */}
          <div className="relative overflow-hidden rounded-3xl border border-orange-100 bg-white/80 backdrop-blur-xl shadow-xl">
            <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-orange-200/60 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-amber-200/60 blur-3xl" />

            <div className="relative p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-orange-600 to-amber-600 text-white px-3 py-1.5 text-xs font-extrabold shadow">
                    <BookOpen size={14} />
                    Lesson Assessment
                  </div>

                  <h1 className="mt-3 text-2xl sm:text-3xl font-extrabold text-slate-900">
                    Lesson {lessonId} Assessment
                  </h1>

                  <p className="mt-1 text-slate-600">
                    {isReflectionOnly
                      ? "Reflection questions to help you think deeply about this lesson."
                      : "Answer all questions to test your understanding of the lesson."}
                  </p>

                  <div className="mt-4 text-sm text-slate-600">
                    Attempts: <span className="font-bold text-slate-900">{attempts}</span>
                    {attempts >= MAX_ATTEMPTS ? (
                      <span className="ml-2 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded-full">
                        Max attempts reached
                      </span>
                    ) : null}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                  <Link
                    to={`/courses/${courseId}/lessons/${lessonId}`}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 font-bold text-slate-800 hover:bg-slate-50 transition"
                  >
                    <ArrowLeft size={18} />
                    Back to Lesson
                  </Link>

                  <button
                    onClick={() => navigate(`/dashboard/learner`)}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 text-white px-4 py-2.5 font-bold hover:opacity-90 transition"
                  >
                    <Home size={18} />
                    Dashboard
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sync banner (if any) */}
          {serverMessage && (
            <div className="mt-4 rounded-2xl border border-yellow-200 bg-yellow-50 text-yellow-900 px-4 py-3 flex items-start justify-between gap-3">
              <div className="text-sm">
                <span className="font-bold">Notice:</span> {serverMessage}
              </div>
              <button
                onClick={() => setServerMessage(null)}
                className="p-1 rounded-lg hover:bg-yellow-100"
                aria-label="Dismiss"
              >
                <X size={18} />
              </button>
            </div>
          )}

          {/* Content */}
          <div className="mt-6 rounded-3xl border border-orange-100 bg-white shadow-xl overflow-hidden">
            {view === "test" && (
              <div className="p-0">
                <TestCard courseId={courseId} lessonId={lessonId} onTestSubmit={handleIframeSubmit} />
              </div>
            )}

            {view === "result" && (
              <div className="p-6 sm:p-8 space-y-6">
                {/* ✅ Congrats panel (only on final lesson completion) */}
                {showCongrats && (
                  <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5 sm:p-6">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-emerald-600 to-green-600 text-white flex items-center justify-center shadow shrink-0">
                          <PartyPopper size={22} />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-lg sm:text-xl font-extrabold text-emerald-900 break-words">
                            Congratulations! 🎉
                          </h3>
                          <p className="text-sm text-emerald-900/80 mt-1 break-words">
                            You have completed this course. Your certificate will be issued by your
                            Instructor or VOP HQ.
                          </p>

                          <div className="mt-4 flex flex-col sm:flex-row gap-3">
                            <button
                              onClick={() => navigate("/courses")}
                              className="inline-flex items-center justify-center rounded-2xl bg-slate-900 text-white px-5 py-3 font-extrabold hover:opacity-90 transition"
                            >
                              Explore other courses
                            </button>

                            <button
                              onClick={() => navigate("/dashboard/learner")}
                              className="inline-flex items-center justify-center rounded-2xl border border-emerald-200 bg-white px-5 py-3 font-extrabold text-emerald-900 hover:bg-emerald-100/50 transition"
                            >
                              Back to Dashboard
                            </button>
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={() => setShowCongrats(false)}
                        className="p-2 rounded-xl hover:bg-emerald-100 transition"
                        aria-label="Dismiss congratulations"
                        title="Dismiss"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Result summary */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h2 className="text-xl font-extrabold text-slate-900">Results</h2>
                      <p className="text-sm text-slate-600 mt-1">
                        {isReflectionOnly
                          ? "Your reflection answers have been submitted."
                          : "Here is your performance for this lesson assessment."}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      {isReflectionOnly ? (
                        <span className="inline-flex items-center gap-2 rounded-full bg-orange-50 text-orange-700 px-3 py-1.5 text-xs font-semibold border border-orange-200">
                          Reflection Submitted
                        </span>
                      ) : passed ? (
                        <span className="inline-flex items-center gap-2 rounded-full bg-green-50 text-green-700 px-3 py-1.5 text-xs font-semibold border border-green-200">
                          <CheckCircle2 size={14} />
                          Passed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-2 rounded-full bg-red-50 text-red-700 px-3 py-1.5 text-xs font-semibold border border-red-200">
                          <XCircle size={14} />
                          Not Passed
                        </span>
                      )}
                    </div>
                  </div>

                  {!isReflectionOnly && (
                    <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
                      <div className="text-sm text-slate-600">
                        Score
                        <div className="text-3xl font-extrabold text-slate-900 mt-1">
                          {statusLoading && safeScore == null ? "Loading…" : `${safeScore ?? 0}%`}
                        </div>
                      </div>

                      <div className="flex-1 min-w-[220px] max-w-md">
                        <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-2 bg-gradient-to-r from-orange-600 to-amber-600 rounded-full"
                            style={{ width: `${safeScore ?? 0}%` }}
                          />
                        </div>
                        <div className="mt-2 text-xs text-slate-500">
                          Pass mark: <span className="font-semibold">{PASS_MARK}%</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
                  <h3 className="text-base font-extrabold text-slate-900">Next steps</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    {passed || isReflectionOnly
                      ? "Continue your learning journey."
                      : "You can review the lesson and retry the assessment."}
                  </p>

                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    {(passed || isReflectionOnly) && hasNextLesson ? (
                      <button
                        onClick={() => navigate(`/courses/${courseId}/lessons/${nextLessonNumber}`)}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-600 to-amber-600 text-white px-5 py-3 font-bold shadow-lg hover:opacity-95 transition"
                      >
                        Continue to Next Lesson →
                      </button>
                    ) : (passed || isReflectionOnly) && !hasNextLesson ? (
                      <button
                        onClick={() => navigate(`/courses/${courseId}`)}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-600 to-amber-600 text-white px-5 py-3 font-bold shadow-lg hover:opacity-95 transition"
                      >
                        Back to Course →
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={handleRetry}
                          disabled={statusLoading}
                          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 text-white px-5 py-3 font-bold hover:opacity-90 transition disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {statusLoading ? "Checking status…" : "Retry Assessment"}
                        </button>

                        <button
                          onClick={() => navigate(`/courses/${courseId}/lessons/${lessonId}`)}
                          className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 font-bold text-slate-800 hover:bg-slate-50 transition"
                        >
                          Back to Lesson
                        </button>
                      </>
                    )}

                    {/* Back to course always available */}
                    <button
                      onClick={() => navigate(`/courses/${courseId}`)}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 font-bold text-slate-800 hover:bg-slate-50 transition"
                    >
                      Back to Course
                    </button>

                    {/* ✅ Certificate removed for learners (we issue via Instructor / VOP HQ now) */}
                  </div>

                  {/* ✅ Optional: a subtle note when course is completed */}
                  {(passed || isReflectionOnly) && isLastLesson && (
                    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                      <span className="font-bold">Note:</span> Certificates are issued by your Instructor or VOP HQ after verification.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Small note (mobile-friendly) */}
          {view === "test" && (
            <div className="mt-4 text-xs text-slate-500 text-center">
              Tip: If the test doesn’t load, refresh the page or check your internet connection.
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}